import { HttpClient } from '@angular/common/http';
import { TranslateLoader, type TranslationObject } from '@ngx-translate/core';
import { PluginTranslationAsset } from '@app/ui/shared';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type TranslationValue = TranslationObject[string];

export class PluginAwareTranslateLoader implements TranslateLoader {
  constructor(
    private readonly http: HttpClient,
    private readonly pluginAssets: readonly PluginTranslationAsset[],
    private readonly appBasePath = './assets/i18n',
  ) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    const sources = [
      this.http.get<TranslationObject>(`${this.appBasePath}/${lang}.json`),
      ...this.pluginAssets
        .filter((asset) => !asset.locales || asset.locales.includes(lang))
        .map((asset) =>
          this.http
            .get<TranslationObject>(`${asset.basePath}/${lang}.json`)
            .pipe(catchError(() => of({} as TranslationObject))),
        ),
    ];

    return forkJoin(sources).pipe(
      map((translations) =>
        translations.reduce((merged, part) => mergeTranslations(merged, part), {} as TranslationObject),
      ),
    );
  }
}

function mergeTranslations(target: TranslationObject, source: TranslationObject): TranslationObject {
  const result: TranslationObject = { ...target };

  for (const [key, value] of Object.entries(source)) {
    const existing = result[key];
    if (isTranslationObject(existing) && isTranslationObject(value)) {
      result[key] = mergeTranslations(existing, value);
    } else {
      result[key] = value as TranslationValue;
    }
  }

  return result;
}

function isTranslationObject(value: TranslationValue): value is TranslationObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
